import { Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { App as CapacitorApp } from "@capacitor/app";

import HomePage from "@/pages/Home";
import CatalogPage from "@/pages/Catalog";
import ActivityPage from "@/pages/Activity";
import ActivityDetailPage from "@/pages/ActivityDetail";
import SettingsPage from "@/pages/Setting";
import NotFound from "@/pages/NotFound";
import Cartpage from "@/pages/Cart";
import PaymentPage from "@/pages/Payment";
import WelcomePage from "@/pages/Welcome";
import LoginPage from "@/pages/Login";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import BarcodeScanPage from "./pages/BarcodeScan";
import { Toaster } from "react-hot-toast";
import { GuestOnly, RequireAuth } from "./middleware/authMiddleware";

import InvoicePDFPage from "./features/InvoicePDF";
import ReceiptPrinter from "./features/InvoicePDF/components/Print";
import PaymentSuccess from "./features/Payment/components/PaymentSuccess";
import CustomerList from "./pages/Customer/CustomerList";
import CustomerDetail from "./pages/Customer/CustomerDetail";
import CustomerEdit from "./pages/Customer/CustomerEdit";
import CustomerAdd from "./pages/Customer/CustomerAdd";
import RevenuePage from "./pages/Revenue";

// =========================
// Theme Provider
// =========================
function ThemeProvider() {
  const location = useLocation();

  useEffect(() => {
    // Jangan terapkan tema di route "/"
    if (location.pathname === "/") return;

    const savedTheme = localStorage.getItem("theme");

    if (savedTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [location.pathname]);

  return null;
}

// =========================
// SafeArea wrapper
// =========================
function SafeArea({ children }) {
  return (
    <div
      style={{
        paddingBottom: 24, // safe area / bottom padding
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  );
}

// =========================
// App
// =========================
function App() {
  const queryClient = new QueryClient();

  useEffect(() => {
    // =========================
    // Handle Android Back Button
    // =========================
    const backHandler = CapacitorApp.addListener("backButton", (e) => {
      e.preventDefault(); // cegah keluar langsung

      // gunakan history browser
      if (window.history.length > 1) {
        window.history.back();
      } else {
        // di halaman root, konfirmasi keluar
        if (window.confirm("Apakah Anda ingin keluar dari aplikasi?")) {
          navigator["app"].exitApp(); // keluar app
        }
      }
    });

    return () => {
      backHandler.remove();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster position="top-center" reverseOrder={false} />
      <ThemeProvider />

      <SafeArea>
        <Routes>
          {/* public */}
          <Route path="/" element={<WelcomePage />} />

          {/* guest only */}
          <Route element={<GuestOnly />}>
            <Route path="/auth/login" element={<LoginPage />} />
          </Route>

          {/* protected */}
          <Route element={<RequireAuth />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/activity/:id" element={<ActivityDetailPage />} />
            <Route path="/setting" element={<SettingsPage />} />
            <Route path="/cart" element={<Cartpage />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/barcode-scan" element={<BarcodeScanPage />} />
            <Route path="/invoice-pdf/:outNo" element={<InvoicePDFPage />} />
            <Route path="/struk/:outNo" element={<ReceiptPrinter />} />
            <Route path="/success/:outNo" element={<PaymentSuccess />} />

            <Route path="/customer" element={<CustomerList />} />
            <Route path="/customer/:id" element={<CustomerDetail />} />
            <Route path="/customer/add" element={<CustomerAdd />} />
            <Route path="/customer/edit/:id" element={<CustomerEdit />} />

            <Route path="/revenue" element={<RevenuePage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </SafeArea>

      {/* <ReactQueryDevtools position="right" /> */}
    </QueryClientProvider>
  );
}

export default App;
